import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Type, Plus } from "lucide-react";
import { drawCaptionFrame } from "@/lib/animator";

const FONTS = ["Montserrat", "Inter", "Poppins", "Anton", "Bebas Neue", "Oswald", "Roboto", "Arial", "Georgia"];
const ANIMS = ["pop", "bouncy", "fade", "slide-up", "slide-down", "slide-left", "slide-right", "kinetic", "punch", "minimal", "motion-blur"];

const uid = () => Math.random().toString(36).slice(2, 10);

const defLine = (over) => ({ text: "", fontFamily: "Anton", fontSize: 110, fillColor: "#FFFFFF", animation: "pop", ...over });

function LineControls({ idx, line, set }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`twoline-controls-${idx}`}>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Line {idx}</div>
      <input value={line.text} onChange={(e) => set({ text: e.target.value })} placeholder={`Line ${idx} text`} data-testid={`twoline-text-${idx}`}
        className="mb-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-[#A1A1AA]">Font</label>
          <select value={line.fontFamily} onChange={(e) => set({ fontFamily: e.target.value })} data-testid={`twoline-font-${idx}`}
            className="w-full rounded-lg border border-white/10 bg-[#141414] px-2 py-1.5 text-xs text-white">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-[#A1A1AA]">Animation</label>
          <select value={line.animation} onChange={(e) => set({ animation: e.target.value })} data-testid={`twoline-anim-${idx}`}
            className="w-full rounded-lg border border-white/10 bg-[#141414] px-2 py-1.5 text-xs text-white">
            {ANIMS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-[#A1A1AA]">Size {line.fontSize}</label>
          <input type="range" min={40} max={200} value={line.fontSize} onChange={(e) => set({ fontSize: parseInt(e.target.value) })} data-testid={`twoline-size-${idx}`} className="w-full" />
        </div>
        <div>
          <label className="text-[10px] text-[#A1A1AA]">Colour</label>
          <input type="color" value={line.fillColor} onChange={(e) => set({ fillColor: e.target.value })} data-testid={`twoline-color-${idx}`} className="h-7 w-full rounded bg-transparent" />
        </div>
      </div>
    </div>
  );
}

export default function TwoLineModal({ open, onOpenChange, config, duration, onAdd }) {
  const [l1, setL1] = useState(defLine({ text: "MATCH DAY", fillColor: "#FFD400" }));
  const [l2, setL2] = useState(defLine({ text: "India vs Australia", fontFamily: "Montserrat", fontSize: 74 }));
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const dur = Math.min(6, Math.max(3, duration || 5));
  const buildObjects = () => ([
    { id: uid(), text: l1.text || "Line 1", start: 0, end: dur, words: [], style: { fontFamily: l1.fontFamily, fontSize: l1.fontSize, fillColor: l1.fillColor }, animation: l1.animation, posX: 0.5, posY: 0.42, scale: 1, opacity: 1, rotation: 0 },
    { id: uid(), text: l2.text || "Line 2", start: 0, end: dur, words: [], style: { fontFamily: l2.fontFamily, fontSize: l2.fontSize, fillColor: l2.fillColor }, animation: l2.animation, posX: 0.5, posY: 0.56, scale: 1, opacity: 1, rotation: 0 },
  ]);

  useEffect(() => {
    if (!open) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const layers = [{ id: "twoline-preview", type: "text", locked: false, hidden: false, objects: buildObjects() }];
    let start = performance.now();
    const loop = (now) => {
      const t = ((now - start) / 1000) % (dur + 0.6);
      ctx.fillStyle = "#0b0b0b"; ctx.fillRect(0, 0, cv.width, cv.height);
      drawCaptionFrame(ctx, t, cv.width, cv.height, layers, config || {}, [], { noClear: true });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [open, l1, l2]);

  const add = () => {
    onAdd(buildObjects());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-white/10 bg-[#0d0d0d] p-0" data-testid="twoline-modal">
        <div className="grid gap-0 md:grid-cols-[1fr_300px]">
          <div className="p-5">
            <DialogTitle className="mb-1 flex items-center gap-2 font-display text-xl font-black text-white"><Type size={18} className="text-[#8B5CF6]" /> Two-Line Title</DialogTitle>
            <p className="mb-4 text-xs text-[#A1A1AA]">Two independent lines — each with its own font, size, colour and animation. Added to your captions as two editable layers you can reposition anytime.</p>
            <div className="space-y-3">
              <LineControls idx={1} line={l1} set={(p) => setL1((s) => ({ ...s, ...p }))} />
              <LineControls idx={2} line={l2} set={(p) => setL2((s) => ({ ...s, ...p }))} />
            </div>
            <button onClick={add} data-testid="twoline-add-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#8B5CF6] py-3 text-sm font-bold text-white hover:bg-[#8B5CF6]/90"><Plus size={16} /> Add Two-Line Title</button>
          </div>
          <div className="flex items-center justify-center bg-[#141414] p-4">
            <div style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.1)" }}>
              <canvas ref={canvasRef} width={270} height={480} className="rounded" data-testid="twoline-preview-canvas" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
